import {
    Alert,
    Box,
    Button,
    Card,
    Code,
    Divider,
    Group,
    Modal,
    SimpleGrid,
    Spoiler,
    Stack,
    Text,
    Title,
} from '@mantine/core';
import type { AdPlatform } from '@marketing-tool/shared';
import { IconSettings } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router';

import { AdControlLogicEditor } from '../../components/AdControlLogicEditor';
import { Page } from '../../components/PageLayout';
import { useAdControlLogic } from '../../data/game-report';
import type { AdGroup, Campaign } from '../../data/game-report/types';
import type { Game } from '../../data/games';
import * as remote from '../../data/remote-data';

import { AdGroupSelector } from './AdGroupSelector';
import { CampaignSelector } from './CampaignSelector';
import { PlatformSelector } from './PlatformSelector';

export const AdControlTab = () => {
    const { game, gameId } = useOutletContext<{ game: Game; gameId: string }>();
    const [platform, setPlatform] = useState<AdPlatform | undefined>(game?.platforms[0]);
    const [campaign, setCampaign] = useState<Campaign | undefined>();
    const [adGroup, setAdGroup] = useState<AdGroup | undefined>();
    const adControlLogic = useAdControlLogic(gameId, platform);
    const [adControlLogicEditorTarget, setAdControlLogicEditorTarget] = useState<
        { type: 'campaigns' | 'adGroups'; id: string } | undefined
    >();

    // Clear selections when upstream selections change
    useEffect(() => setPlatform(game?.platforms[0]), [game]);
    useEffect(() => setCampaign(undefined), [game, platform]);
    useEffect(() => setAdGroup(undefined), [campaign]);

    const hasAnyLogic = remote.withDefault(
        remote.map(
            adControlLogic.data,
            (data) => Object.keys(data.campaigns).length > 0 || Object.keys(data.adGroups).length > 0,
        ),
        false,
    );

    return (
        <Page>
            <Stack gap="xl">
                {/* Configuration Section */}
                <Card withBorder>
                    <Box p="md" pb={0}>
                        <Title order={4}>Control Logic Configuration</Title>
                        <Text c="dimmed">
                            Configure automated rules to control ad delivery based on performance metrics
                        </Text>
                    </Box>
                    <Divider my="sm" />
                    <Box p="md" pt={0}>
                        <Stack gap="lg">
                            {/* Primary Configuration Grid */}
                            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl">
                                {/* Platform and Campaign Selection */}
                                <Stack gap="lg">
                                    <Box pos="relative">
                                        <Text fw={500} mb={4}>
                                            Platform
                                        </Text>
                                        <PlatformSelector
                                            id="platform"
                                            gameId={gameId}
                                            value={platform}
                                            onChange={setPlatform}
                                        />
                                    </Box>

                                    {platform && (
                                        <Box pos="relative">
                                            <Group justify="space-between" mb={4}>
                                                <Text fw={500}>Campaign</Text>
                                                {campaign && (
                                                    <Button
                                                        variant="subtle"
                                                        size="compact-xs"
                                                        onClick={() => setCampaign(undefined)}
                                                    >
                                                        Clear
                                                    </Button>
                                                )}
                                            </Group>
                                            <CampaignSelector
                                                id="campaign"
                                                gameId={gameId}
                                                platform={platform}
                                                value={campaign?.id}
                                                onChange={setCampaign}
                                            />
                                            {campaign && (
                                                <Box mt={12}>
                                                    <Button
                                                        variant="outline"
                                                        fullWidth
                                                        leftSection={<IconSettings size={16} />}
                                                        onClick={() =>
                                                            setAdControlLogicEditorTarget({
                                                                type: 'campaigns',
                                                                id: campaign.id.toString(),
                                                            })
                                                        }
                                                    >
                                                        Edit Campaign Logic
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    )}

                                    {platform && campaign && (
                                        <Box pos="relative">
                                            <Group justify="space-between" mb={4}>
                                                <Text fw={500}>Ad Group</Text>
                                                {adGroup && (
                                                    <Button
                                                        variant="subtle"
                                                        size="compact-xs"
                                                        onClick={() => setAdGroup(undefined)}
                                                    >
                                                        Clear
                                                    </Button>
                                                )}
                                            </Group>
                                            <AdGroupSelector
                                                id="ad-group"
                                                gameId={gameId}
                                                platform={platform}
                                                campaignId={campaign.id}
                                                value={adGroup?.id}
                                                onChange={setAdGroup}
                                            />
                                            {adGroup && (
                                                <Box mt={12}>
                                                    <Button
                                                        variant="outline"
                                                        fullWidth
                                                        leftSection={<IconSettings size={16} />}
                                                        onClick={() =>
                                                            setAdControlLogicEditorTarget({
                                                                type: 'adGroups',
                                                                id: adGroup.id.toString(),
                                                            })
                                                        }
                                                    >
                                                        Edit Ad Group Logic
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Stack>
                            </SimpleGrid>
                        </Stack>
                    </Box>
                </Card>

                {/* Logic Overview */}
                {hasAnyLogic && (
                    <Alert color="blue" title="Control Logic Active">
                        <Text>
                            Automated rules are currently managing ad delivery for this game. Logic rules are actively
                            monitoring performance metrics and automatically adjusting ad delivery to optimize campaign
                            performance.
                        </Text>
                    </Alert>
                )}

                {/* Inline Help */}
                <Spoiler maxHeight={0} showLabel="Show DSL reference" hideLabel="Hide DSL reference">
                    <Card withBorder>
                        <Box p="md">
                            <Stack gap="md">
                                <Title order={5}>Ad Control Logic DSL</Title>
                                <Text>
                                    Ad control logic is a simple domain-specific language (DSL) for controlling the
                                    delivery of ads.
                                </Text>
                                <Box>
                                    <Text mb="xs">Here is an example that uses all available features:</Text>
                                    <Code block>{`if cpx > 2 and installs > 5 and cost > 10 and clicks > 40 then
  paused
else
  if installs <= 0 and cost > 10 and clicks > 40 then
    paused
  else
    skip`}</Code>
                                </Box>
                                <Box>
                                    <Text mb="xs">Alternatively, this could be written as:</Text>
                                    <Code
                                        block
                                    >{`if (cpx > 2 and installs > 5 and cost > 10 and clicks > 40) or (installs <= 0 and cost > 10 and clicks > 40) then
  paused
else
  skip`}</Code>
                                </Box>
                                <Text>
                                    You don't need to worry too much about formatting: it will be formatted
                                    automatically when you save.
                                </Text>
                            </Stack>
                        </Box>
                    </Card>
                </Spoiler>

                {/* Ad Control Logic Editor Modal */}
                {adControlLogicEditorTarget && (
                    <Modal
                        opened={true}
                        onClose={() => setAdControlLogicEditorTarget(undefined)}
                        size="lg"
                        title="Edit ad control logic"
                    >
                        {remote.matchSome(
                            adControlLogic.data,
                            {
                                Success: (data) => {
                                    const { type, id } = adControlLogicEditorTarget;
                                    const { days, logic, allowAutoStart, minActiveAdCount } = data[type][id] ?? {
                                        days: 1,
                                        logic: '',
                                        allowAutoStart: true,
                                        minActiveAdCount: 15,
                                    };
                                    return (
                                        <AdControlLogicEditor
                                            days={days}
                                            logic={logic}
                                            allowAutoStart={allowAutoStart}
                                            minActiveAdCount={minActiveAdCount}
                                            onSave={async ({ days, logic, allowAutoStart, minActiveAdCount }) => {
                                                await adControlLogic.update(
                                                    type,
                                                    id,
                                                    days,
                                                    logic,
                                                    allowAutoStart,
                                                    minActiveAdCount,
                                                );
                                                setAdControlLogicEditorTarget(undefined);
                                            }}
                                            onCancel={() => setAdControlLogicEditorTarget(undefined)}
                                            onDelete={async () => {
                                                await adControlLogic.delete(type, id);
                                                setAdControlLogicEditorTarget(undefined);
                                            }}
                                        />
                                    );
                                },
                            },
                            () => null,
                        )}
                    </Modal>
                )}
            </Stack>
        </Page>
    );
};
